/**
 * The regions a work mobile can come from (migration 0139 — any country).
 *
 * The list is the set libphonenumber carries metadata for, copied here rather
 * than importing its metadata into the dashboard bundle: the server parses and
 * validates every number, so the client only needs names to pick from.
 * Region names come from the browser's own `Intl.DisplayNames`, in the reader's
 * language, falling back to the code.
 */
const REGIONS = `AC AD AE AF AG AI AL AM AO AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM
BN BO BQ BR BS BT BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM
DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GT GU GW
GY HK HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA
LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY
MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PR PS PT PW PY QA RE RO RS RU
RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TA TC TD TG TH TJ TK TL TM TN
TO TR TT TV TW TZ UA UG US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW`
  .split(/\s+/)
  .filter(Boolean);

function regionName(code: string, names: Intl.DisplayNames | null): string {
  try {
    return names?.of(code) ?? code;
  } catch {
    return code;
  }
}

let cached: { value: string; label: string }[] | null = null;

/** Every region as a select option, sorted by name. */
export function phoneRegionOptions(): { value: string; label: string }[] {
  if (cached) return cached;
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames(undefined, { type: 'region' });
  } catch {
    names = null;
  }
  cached = REGIONS.map((code) => ({ value: code, label: regionName(code, names) })).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
  return cached;
}

/**
 * An example number for the field, by region; generic elsewhere. Only numbers a
 * regulator reserves for fiction, so nobody copying one texts a stranger:
 * ACMA's 0491 570 range (AU), Ofcom's 07700 900 range (GB), 555-01xx (US/CA).
 * New Zealand publishes no such range, so it gets words, not a number.
 */
export function phonePlaceholder(region: string): string {
  switch (region.toUpperCase()) {
    case 'AU':
      return '0491 570 156';
    case 'NZ':
      return 'Mobile number, starting 02';
    case 'GB':
      return '07700 900123';
    case 'US':
    case 'CA':
      return '(201) 555-0123';
    default:
      return 'Mobile number, or +country code';
  }
}

/** A region code we can offer, or the fallback. */
export function knownRegion(code: string | null | undefined, fallback = 'AU'): string {
  const upper = (code ?? '').toUpperCase();
  return REGIONS.includes(upper) ? upper : fallback;
}
